import _ from 'lodash';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_DURATION, OutputType } from '@/tools/onnx/onnx.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { OnnxResultType } from '@/tools/onnx/onnx.constants';
import type { OnnxInputParameter } from '@/tools/onnx/onnx.utilities';
import type {
  BaseToolPersistedState,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';

export interface DurationItem {
  value: number;
  units: string;
}

export interface OnnxToolStoreState extends BaseToolPersistedState {
  color: string;
  formulaItemId: string | null;
  onnxResultType: OnnxResultType | null;
  onnxInputParameters: OnnxInputParameter[];
  outputType: OutputType;
  outputSignal: string | null;
  cleanCapsules: boolean;
  capsuleMinDuration: DurationItem;
  capsuleMergeDuration: DurationItem;
  restrictToCondition?: ToolItem | null;
}

export interface OnnxToolStoreActions {
  setColor: (color: string) => void;
  setOutputType: (outputType: OutputType) => void;
  setFormulaItemId: (formulaItemId: string | null) => void;
  setOnnxResultType: (onnxResultType: OnnxResultType | null) => void;
  setOnnxInputParameters: (parameters: OnnxInputParameter[]) => void;
  updateOnnxInputParameter: (parameter: OnnxInputParameter) => void;
  setOutputSignal: (outputSignal: string | null) => void;
  setCleanCapsules: (cleanCapsules: boolean) => void;
  setCapsuleMinDuration: (capsuleMinDuration: DurationItem) => void;
  setCapsuleMergeDuration: (capsuleMergeDuration: DurationItem) => void;
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (advancedParametersCollapsed: boolean) => void;
  setRestrictToCondition: (item: ToolItem | null | undefined) => void;
  rehydrateForEdit: (
    payload: ToolRehydratePayload,
    formulaItemId: string | null,
    onnxInputParameters: OnnxInputParameter[],
  ) => void;
  reset: () => void;
}

export type OnnxToolStoreDehydratedState = OnnxToolStoreState;

const parameterDefinitions: ParameterDefinitions<'restrictToCondition'> = {
  restrictToCondition: {
    predicate: ['name', 'restrictToCondition'],
  },
};

const initialState: OnnxToolStoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  color: '',
  formulaItemId: null,
  onnxResultType: null,
  onnxInputParameters: [],
  outputType: OutputType.CONDITION,
  outputSignal: null,
  cleanCapsules: false,
  capsuleMinDuration: DEFAULT_DURATION,
  capsuleMergeDuration: DEFAULT_DURATION,
  restrictToCondition: undefined,
});

const stripItemFromInputParameters = (onnxInputParameters: OnnxInputParameter[]) =>
  onnxInputParameters.map(({ identifier, name }) => ({ identifier, name }));

const onnxConfigParamsSelector = createSelector(
  [(state: OnnxToolStoreState) => state],
  (state): ToolConfigParams =>
    getToolConfigParams({
      serializedState: {
        ..._.omit(state, ['formulaItemId']),
        onnxInputParameters: stripItemFromInputParameters(state.onnxInputParameters),
      } as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.ONNX,
      parameterDefinitionKeys: Object.keys(parameterDefinitions),
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    }),
);

export const dehydrateOnnxToolStoreState = (state: OnnxToolStoreState): OnnxToolStoreDehydratedState => state;

const onnxActionsFactory: ActionsFactory<OnnxToolStoreState, OnnxToolStoreActions> = (set, _get, resetState) => ({
  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setOutputType: (outputType) =>
    set((draft) => {
      draft.outputType = outputType;
    }),

  setFormulaItemId: (formulaItemId) =>
    set((draft) => {
      draft.formulaItemId = formulaItemId;
    }),

  setOnnxResultType: (onnxResultType) =>
    set((draft) => {
      draft.onnxResultType = onnxResultType;
    }),

  setOnnxInputParameters: (parameters) =>
    set((draft) => {
      if (parameters.length === 0 && draft.onnxInputParameters.length === 0) {
        return;
      }

      draft.onnxInputParameters = parameters;
    }),

  updateOnnxInputParameter: (parameter) =>
    set((draft) => {
      const index = _.findIndex(draft.onnxInputParameters, { identifier: parameter.identifier });

      if (index === -1) {
        throw new Error(`Onnx input parameter with identifier ${parameter.identifier} not found.`);
      }
      draft.onnxInputParameters[index] = {
        ...draft.onnxInputParameters[index],
        ...parameter,
        item: sanitizeToolItem(parameter.item, ['id', 'name', 'type']) as OnnxInputParameter['item'],
      };
    }),

  setOutputSignal: (outputSignal) =>
    set((draft) => {
      draft.outputSignal = outputSignal;
    }),

  setCleanCapsules: (cleanCapsules) =>
    set((draft) => {
      draft.cleanCapsules = cleanCapsules;
    }),

  setCapsuleMinDuration: (capsuleMinDuration) =>
    set((draft) => {
      draft.capsuleMinDuration = capsuleMinDuration;
    }),

  setCapsuleMergeDuration: (capsuleMergeDuration) =>
    set((draft) => {
      draft.capsuleMergeDuration = capsuleMergeDuration;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (advancedParametersCollapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = advancedParametersCollapsed;
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(item) as OnnxToolStoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload, formulaItemId, onnxInputParameters) => {
    resetState();
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });

      Object.assign(draft, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
        formulaItemId,
        onnxInputParameters,
      });
    });
  },

  reset: resetState,
});

const STORE_NAME = 'Onnx';

const [OnnxStoreProvider, useOnnxToolStoreInstance, useOnnxToolStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  onnxActionsFactory,
);

export { OnnxStoreProvider };

export const useOnnxToolId = () => useOnnxToolStoreSelector((state) => state.id);
export const useOnnxToolName = () => useOnnxToolStoreSelector((state) => state.name);
export const useOnnxToolOriginalParameters = () => useOnnxToolStoreSelector((state) => state.originalParameters);
export const useOnnxToolAdvancedParametersCollapsed = () =>
  useOnnxToolStoreSelector((state) => state.advancedParametersCollapsed);
export const useOnnxToolConfigVersion = () => useOnnxToolStoreSelector((state) => state.configVersion);
export const useOnnxToolColor = () => useOnnxToolStoreSelector((state) => state.color);
export const useOnnxToolFormulaItemId = () => useOnnxToolStoreSelector((state) => state.formulaItemId);
export const useOnnxToolResultType = () => useOnnxToolStoreSelector((state) => state.onnxResultType);
export const useOnnxToolInputParameters = () => useOnnxToolStoreSelector((state) => state.onnxInputParameters);
export const useOnnxToolRestrictToCondition = () => useOnnxToolStoreSelector((state) => state.restrictToCondition);
export const useOnnxToolOutputType = () => useOnnxToolStoreSelector((state) => state.outputType);
export const useOnnxToolOutputSignal = () => useOnnxToolStoreSelector((state) => state.outputSignal);
export const useOnnxToolCleanCapsules = () => useOnnxToolStoreSelector((state) => state.cleanCapsules);
export const useOnnxToolCapsuleMinDuration = () => useOnnxToolStoreSelector((state) => state.capsuleMinDuration);
export const useOnnxToolCapsuleMergeDuration = () => useOnnxToolStoreSelector((state) => state.capsuleMergeDuration);
export const useOnnxToolConfigParams = () => useOnnxToolStoreSelector(onnxConfigParamsSelector);

export const useOnnxToolActions = () => useOnnxToolStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestOnnxToolStore(overrides?: Partial<OnnxToolStoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestOnnxToolStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, onnxActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateOnnxToolStore() {
  return createStoreInstance<OnnxToolStoreState, OnnxToolStoreActions>(initialState, onnxActionsFactory, {
    storeName: STORE_NAME,
  });
}

/**
 * Registers the OnnxTool store with the persistence pipeline so the persisted subset survives across worksheet loads.
 */
export const usePersistOnnxToolStore = () => {
  const register = useRegisterPersistedStore();
  const store = useOnnxToolStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'onnxStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateOnnxToolStoreState,
    });
  }, [register, store]);
};
